import type { AllergenKey, Badge as BadgeKey, DietaryKey, Product } from '@cartazo/contracts'

/**
 * Primitivas propias en lugar de Radix.
 *
 * El panel de administración sí usa Radix, donde el peso no importa. Aquí el
 * presupuesto de JavaScript de la primera vista son 60 KB, y estos componentes
 * ocupan unos 4 KB haciendo lo que la carta necesita.
 */

// ── Iconos ─────────────────────────────────────────────────────────────────

/**
 * Iconos como SVG inline en vez de emoji.
 *
 * Un emoji lo pinta la fuente del sistema con sus propios colores: 🔍 sale azul
 * y 🛒 multicolor, que choca con los cuatro temas y arruina la sobriedad de una
 * carta de restaurante. Además cambia de forma entre Android, iOS y Windows.
 * Con `currentColor`, el icono hereda el color del tema y es idéntico en todas
 * partes por unos pocos bytes.
 */
const ICON_PATHS = {
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM16 16l4.5 4.5',
  menu: 'M4 7h16M4 12h16M4 17h16',
  cart: 'M3 4h2l2.4 10.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm9 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  back: 'M15 5l-7 7 7 7',
  close: 'M6 6l12 12M18 6L6 18',
} as const

export function Icon({ name }: { name: keyof typeof ICON_PATHS }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      // El nombre accesible lo aporta el aria-label del botón que lo contiene;
      // anunciarlo dos veces sería ruido para un lector de pantalla.
      aria-hidden="true"
      focusable="false"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  )
}

// ── Etiquetas de producto ──────────────────────────────────────────────────

const BADGE_LABELS: Record<BadgeKey, string> = {
  star: 'Plato Estrella',
  chef: 'Recomendación del Chef',
  sommelier: 'Sugerencia del Sommelier',
  new: 'Nuevo',
}

const DIETARY_LABELS: Record<DietaryKey, { short: string; full: string }> = {
  vegan: { short: 'VG', full: 'Vegano' },
  vegetarian: { short: 'V', full: 'Vegetariano' },
  gluten_free: { short: 'SG', full: 'Sin gluten' },
  lactose_free: { short: 'SL', full: 'Sin lactosa' },
  halal: { short: 'HAL', full: 'Halal' },
  kosher: { short: 'KSH', full: 'Kosher' },
}

const ALLERGEN_LABELS: Record<AllergenKey, string> = {
  gluten: 'Gluten',
  crustaceans: 'Crustáceos',
  eggs: 'Huevo',
  fish: 'Pescado',
  peanuts: 'Cacahuete',
  soy: 'Soja',
  dairy: 'Lácteos',
  nuts: 'Frutos secos',
  celery: 'Apio',
  mustard: 'Mostaza',
  sesame: 'Sésamo',
  sulphites: 'Sulfitos',
  lupin: 'Altramuces',
  molluscs: 'Moluscos',
}

export function ProductBadges({ product }: { product: Product }) {
  const hasAny =
    product.badge ||
    product.dietary.length > 0 ||
    product.spice_level > 0 ||
    product.allergens.length > 0 ||
    !product.is_available

  if (!hasAny) return null

  return (
    <div className="badge-row">
      {!product.is_available && <span className="badge badge--soldout">Agotado por hoy</span>}

      {product.badge && <span className="badge">{BADGE_LABELS[product.badge]}</span>}

      {product.dietary.map((key) => (
        // El texto completo va en `title` y en aria-label: un icono o una sigla
        // sola no es accesible, y con alergias la ambigüedad es peligrosa.
        <span key={key} className="badge badge--outline" title={DIETARY_LABELS[key].full}>
          <span aria-hidden="true">{DIETARY_LABELS[key].short}</span>
          <span className="sr-only">{DIETARY_LABELS[key].full}</span>
        </span>
      ))}

      {product.spice_level > 0 && (
        <span className="badge badge--outline" title={`Picante nivel ${product.spice_level} de 3`}>
          <span aria-hidden="true">{'🌶️'.repeat(product.spice_level)}</span>
          <span className="sr-only">Picante nivel {product.spice_level} de 3</span>
        </span>
      )}

      {product.allergens.length > 0 && (
        <span className="badge badge--outline">
          Contiene:{' '}
          {product.allergens
            .map((a) => `${ALLERGEN_LABELS[a.key]}${a.is_trace ? ' (trazas)' : ''}`)
            .join(', ')}
        </span>
      )}
    </div>
  )
}

export { ALLERGEN_LABELS, BADGE_LABELS, DIETARY_LABELS }

/**
 * Fondo de imagen con degradado de respaldo.
 *
 * El `fallback` del contrato cubre los dos casos que dejan un hueco blanco: que
 * el plato no tenga foto, y que la foto falle al cargar. En una carta, un
 * rectángulo vacío se lee como un error de la app.
 */
export function BackdropImage({
  image,
  fallback,
  className,
  eager,
  alive,
}: {
  image: { src: string; srcset: string; focus?: string } | null
  fallback: string
  className: string
  eager?: boolean
  /**
   * Deriva lentamente (Ken Burns). Cuesta CERO bytes: es CSS sobre la misma
   * imagen, y da a una foto quieta el movimiento que hace que una carta
   * parezca viva sin descargar un vídeo.
   *
   * Solo donde se ve UNA foto a la vez. Doce animaciones simultáneas en una
   * rejilla queman batería sin que nadie las mire.
   */
  alive?: boolean
}) {
  if (!image) {
    return <div className={className} style={{ background: fallback }} aria-hidden="true" />
  }

  return (
    <div className={className} style={{ background: fallback }} aria-hidden="true">
      <img
        src={image.src}
        srcSet={image.srcset}
        sizes="100vw"
        alt=""
        // La primera imagen es el LCP: se prioriza. El resto se difiere para no
        // competir por ancho de banda en 3G.
        fetchPriority={eager ? 'high' : 'low'}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        // La animación va en CSS y está guardada por `prefers-reduced-motion`:
        // a quien marca esa preferencia el movimiento le sienta mal de verdad.
        className={alive ? 'kenburns' : undefined}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // El punto focal decide POR DÓNDE recorta `cover`. Sin esto
          // recortaba siempre por el centro y un plato descentrado se salía
          // del cuadro sin que hubiera forma de arreglarlo desde el panel.
          objectPosition: image.focus ?? '50% 50%',
        }}
        onError={(event) => {
          // Al fallar, se retira la imagen y queda el degradado del contenedor.
          event.currentTarget.style.display = 'none'
        }}
      />
    </div>
  )
}
